/**
 * Integrytas Group - Base App (MOBILE)
 * Page: UNE.ADMIN — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/UneAdminPage.tsx. That desktop page
 * is a dense multi-section editor (Workspaces, Event Categories, Mandatory Rules,
 * Role/Global Defaults) driven by inline grids and modal selects. This screen is a
 * DISTINCT presentation over the SAME data/logic layer only:
 *   - reads via apiDb (une_workspace / une_event_category / une_mandatory_rule /
 *     une_role_default + csm_role_master for role labels), resolving every FK to a
 *     name — category → name, workspace → name, role → translated _tl display text
 *     against the ACTIVE locale (§8, §17.B4 — never a raw id reaches render),
 *   - the activate/deactivate write routes through the Common Routine
 *     (routedUpdate) — §2 single write path; never a direct apiDb write.
 * A KPI strip summarises the four config areas; a segment control switches the list
 * between them; a bottom-sheet shows the resolved detail. It never re-renders the
 * desktop grids; §16. Structural editing (add/delete, channel/preference changes,
 * translation editing) stays on the desktop.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, LayoutGrid, Tag, Lock, Users } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { routedUpdate } from '../../lib/routed_writes';
import { MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow, MobileFilterChips, MobileStatusPill } from '../components';
import type { MobileLeadTone } from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'UNE.ADMIN';

// ---------------------------------------------------------------------------
// Row shapes — mirror the exact columns the desktop UneAdminPage selects.
// ---------------------------------------------------------------------------

type YN = 'Y' | 'N';
type MandChannel = 'INAPP' | 'EMAIL';
type Pref = 'INAPP_ONLY' | 'EMAIL_ONLY' | 'BOTH' | 'NONE';

interface WsRow { une_workspace_id: number; internal_code: string; name: string; is_default: YN; is_active: YN }
interface CatRow { une_event_category_id: number; internal_code: string; name: string; sort_order: number; is_active: YN }
interface MandRow { une_mandatory_rule_id: number; une_event_category_id: number; channel: MandChannel; une_workspace_id: number | null; is_active: YN }
interface RdRow { une_role_default_id: number; une_event_category_id: number; role_id: number | null; une_workspace_id: number | null; channel_pref: Pref; is_active: YN }
interface TlRow { locale_code: string; display_text: string }

type Segment = 'workspaces' | 'categories' | 'mandatory' | 'defaults';
type StatusFacet = 'all' | 'active' | 'inactive';

// CHECK-enum labels (§17.B11) — defined once, rendered via t(), never bare literals.
const CHANNEL_LABELS: { value: MandChannel; labelKey: string; fallback: string }[] = [
  { value: 'INAPP', labelKey: 'mobile.une.channel.INAPP', fallback: 'In-App' },
  { value: 'EMAIL', labelKey: 'mobile.une.channel.EMAIL', fallback: 'Email' },
];
const PREF_LABELS: { value: Pref; labelKey: string; fallback: string }[] = [
  { value: 'INAPP_ONLY', labelKey: 'mobile.une.pref.INAPP_ONLY', fallback: 'In-App only' },
  { value: 'EMAIL_ONLY', labelKey: 'mobile.une.pref.EMAIL_ONLY', fallback: 'Email only' },
  { value: 'BOTH', labelKey: 'mobile.une.pref.BOTH', fallback: 'In-App + Email' },
  { value: 'NONE', labelKey: 'mobile.une.pref.NONE', fallback: 'None' },
];

// A unified card the list/detail can render regardless of which config area it came
// from. table/pkColumn/pk carry enough for the routed activate/deactivate write.
interface UneCard {
  key: string;
  table: string;
  pkColumn: string;
  pk: number;
  segment: Segment;
  title: string;
  em: string;          // secondary identifier line for the sheet header
  subtitle: string;    // secondary line in the list row
  is_active: YN;
  fields: { k: string; v: ReactNode }[];
  search: string;
}

export function MobileUneAdminPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const activeLocale = useActiveLocale();

  const [ws, setWs] = useState<WsRow[]>([]);
  const [cats, setCats] = useState<CatRow[]>([]);
  const [mand, setMand] = useState<MandRow[]>([]);
  const [rds, setRds] = useState<RdRow[]>([]);
  const [roleNames, setRoleNames] = useState<Record<number, string>>({});

  const [segment, setSegment] = useState<Segment>('workspaces');
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [selected, setSelected] = useState<UneCard | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const [w, c, m, r, ro] = await Promise.all([
        apiDb.from('une_workspace').select('une_workspace_id, internal_code, name, is_default, is_active').order('internal_code'),
        apiDb.from('une_event_category').select('une_event_category_id, internal_code, name, sort_order, is_active').order('sort_order'),
        apiDb.from('une_mandatory_rule').select('une_mandatory_rule_id, une_event_category_id, channel, une_workspace_id, is_active').order('une_mandatory_rule_id'),
        apiDb.from('une_role_default').select('une_role_default_id, une_event_category_id, role_id, une_workspace_id, channel_pref, is_active').order('une_role_default_id'),
        apiDb.from('csm_role_master').select('role_id, internal_code, csm_role_master_tl ( locale_code, display_text )').order('internal_code'),
      ]);
      if (w.error) throw w.error;
      if (c.error) throw c.error;
      if (m.error) throw m.error;
      if (r.error) throw r.error;
      if (ro.error) throw ro.error;

      setWs((w.data ?? []) as WsRow[]);
      setCats((c.data ?? []) as CatRow[]);
      setMand((m.data ?? []) as MandRow[]);
      setRds((r.data ?? []) as RdRow[]);

      // Resolve role_id → translated role name (§17.B4) against the active locale.
      type RawRole = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
      const names: Record<number, string> = {};
      for (const role of (ro.data ?? []) as RawRole[]) {
        const tls = role.csm_role_master_tl ?? [];
        const exact = tls.find((tl) => tl.locale_code === activeLocale);
        const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
        names[role.role_id] = exact?.display_text ?? fb?.display_text ?? role.internal_code;
      }
      setRoleNames(names);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // ---- FK / enum resolvers (never render a raw id or bare enum) --------------
  const catName = (id: number) => cats.find((x) => x.une_event_category_id === id)?.name ?? t('mobile.une.unknown', 'Unknown');
  const wsName = (id: number | null) =>
    id == null ? t('mobile.une.all_workspaces', 'All workspaces') : (ws.find((x) => x.une_workspace_id === id)?.name ?? t('mobile.une.unknown', 'Unknown'));
  const roleName = (id: number | null) =>
    id == null ? t('mobile.une.global', 'Global') : (roleNames[id] ?? t('mobile.une.unknown', 'Unknown'));
  const channelLabel = (v: MandChannel) => {
    const entry = CHANNEL_LABELS.find((x) => x.value === v);
    return entry ? t(entry.labelKey, entry.fallback) : v;
  };
  const prefLabel = (v: Pref) => {
    const entry = PREF_LABELS.find((x) => x.value === v);
    return entry ? t(entry.labelKey, entry.fallback) : v;
  };
  const statusPill = (active: YN) => (
    <MobileStatusPill tone={active === 'Y' ? 'ok' : 'no'}>
      {active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
    </MobileStatusPill>
  );

  // ---- Build the unified card list for the active segment -------------------
  const cards: UneCard[] = useMemo(() => {
    if (segment === 'workspaces') {
      return ws.map((w) => ({
        key: `ws-${w.une_workspace_id}`,
        table: 'une_workspace', pkColumn: 'une_workspace_id', pk: w.une_workspace_id, segment,
        title: w.name || w.internal_code,
        em: w.internal_code,
        subtitle: w.internal_code + (w.is_default === 'Y' ? ' · ' + t('mobile.une.default', 'Default') : ''),
        is_active: w.is_active,
        fields: [
          { k: t('mobile.une.code', 'Code'), v: w.internal_code },
          { k: t('mobile.une.default', 'Default'), v: w.is_default === 'Y'
            ? <MobileStatusPill tone="ok">{t('mobile.une.default', 'Default')}</MobileStatusPill>
            : t('common.standard', 'Standard') },
        ],
        search: `${w.name} ${w.internal_code}`.toLowerCase(),
      }));
    }
    if (segment === 'categories') {
      return cats.map((c) => ({
        key: `cat-${c.une_event_category_id}`,
        table: 'une_event_category', pkColumn: 'une_event_category_id', pk: c.une_event_category_id, segment,
        title: c.name || c.internal_code,
        em: c.internal_code,
        subtitle: `${c.internal_code} · ${t('mobile.une.sort_order', 'Order')} ${c.sort_order}`,
        is_active: c.is_active,
        fields: [
          { k: t('mobile.une.code', 'Code'), v: c.internal_code },
          { k: t('mobile.une.sort_order', 'Order'), v: String(c.sort_order) },
        ],
        search: `${c.name} ${c.internal_code}`.toLowerCase(),
      }));
    }
    if (segment === 'mandatory') {
      return mand.map((m) => ({
        key: `mand-${m.une_mandatory_rule_id}`,
        table: 'une_mandatory_rule', pkColumn: 'une_mandatory_rule_id', pk: m.une_mandatory_rule_id, segment,
        title: catName(m.une_event_category_id),
        em: channelLabel(m.channel),
        subtitle: `${channelLabel(m.channel)} · ${wsName(m.une_workspace_id)}`,
        is_active: m.is_active,
        fields: [
          { k: t('mobile.une.category', 'Category'), v: catName(m.une_event_category_id) },
          { k: t('mobile.une.channel_label', 'Channel'), v: channelLabel(m.channel) },
          { k: t('mobile.une.field.workspace', 'Workspace'), v: wsName(m.une_workspace_id) },
        ],
        search: `${catName(m.une_event_category_id)} ${channelLabel(m.channel)} ${wsName(m.une_workspace_id)}`.toLowerCase(),
      }));
    }
    return rds.map((r) => ({
      key: `rd-${r.une_role_default_id}`,
      table: 'une_role_default', pkColumn: 'une_role_default_id', pk: r.une_role_default_id, segment,
      title: catName(r.une_event_category_id),
      em: roleName(r.role_id),
      subtitle: `${roleName(r.role_id)} · ${prefLabel(r.channel_pref)}`,
      is_active: r.is_active,
      fields: [
        { k: t('mobile.une.category', 'Category'), v: catName(r.une_event_category_id) },
        { k: t('mobile.une.role', 'Role'), v: roleName(r.role_id) },
        { k: t('mobile.une.field.workspace', 'Workspace'), v: wsName(r.une_workspace_id) },
        { k: t('mobile.une.preference', 'Preference'), v: prefLabel(r.channel_pref) },
      ],
      search: `${catName(r.une_event_category_id)} ${roleName(r.role_id)} ${prefLabel(r.channel_pref)}`.toLowerCase(),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segment, ws, cats, mand, rds, roleNames, t]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return cards.filter((card) => {
      if (statusFacet === 'active' && card.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && card.is_active !== 'N') return false;
      if (!q) return true;
      return card.search.includes(q);
    });
  }, [cards, filter, statusFacet]);

  const segLead = (s: Segment): { tone: MobileLeadTone; icon: ReactNode } => {
    switch (s) {
      case 'workspaces': return { tone: 'blue', icon: <LayoutGrid /> };
      case 'categories': return { tone: 'slate', icon: <Tag /> };
      case 'mandatory': return { tone: 'amber', icon: <Lock /> };
      case 'defaults': return { tone: 'green', icon: <Users /> };
    }
  };

  const segments: { key: Segment; label: string }[] = [
    { key: 'workspaces', label: t('mobile.une.seg.workspaces', 'Workspaces') },
    { key: 'categories', label: t('mobile.une.seg.categories', 'Categories') },
    { key: 'mandatory', label: t('mobile.une.seg.mandatory', 'Mandatory') },
    { key: 'defaults', label: t('mobile.une.seg.defaults', 'Defaults') },
  ];

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.une.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleActive = async (card: UneCard) => {
    setActionBusy(true);
    setError(null);
    const next: YN = card.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, card.table, card.pkColumn, card.pk, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  const activeLead = segLead(segment);

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.une.title', 'Notification Admin')}
        description={t('mobile.une.description', 'Workspaces, event categories, mandatory rules and role defaults for notifications.')}
      />

      <MobileStatStrip
        stats={[
          { value: ws.length, label: t('mobile.une.seg.workspaces', 'Workspaces') },
          { value: cats.length, label: t('mobile.une.seg.categories', 'Categories') },
          { value: mand.length, label: t('mobile.une.seg.mandatory', 'Mandatory') },
          { value: rds.length, label: t('mobile.une.seg.defaults', 'Defaults') },
        ]}
      />

      <MobileFilterChips
        chips={segments.map((s) => ({ value: s.key, label: s.label }))}
        isActive={(v) => segment === v}
        onSelect={(v) => { setSegment(v as Segment); setSelected(null); }}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.une.search', 'Search configuration')}
          aria-label={t('mobile.une.search', 'Search configuration')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.une.none', 'Nothing matches')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((card) => (
            <MobileCardRow
              key={card.key}
              lead={activeLead.icon}
              leadTone={activeLead.tone}
              title={card.title}
              subtitle={card.subtitle}
              trailing={statusPill(card.is_active)}
              chevron
              onClick={() => setSelected(card)}
            />
          ))}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.une.detail', 'Notification setting')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av">{segLead(selected.segment).icon}</span>
              <span>
                <div className="m-nm">{selected.title}</div>
                <div className="m-em">{selected.em}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow label={t('mobile.une.status', 'Status')} value={statusPill(selected.is_active)} />
              {selected.fields.map((f) => (
                <MobileDetailRow key={f.k} label={f.k} value={f.v} />
              ))}
            </MobileContentCard>
            <div className="m-actionbar">
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-primary')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
